/**
 * workAccidentSlice — Aléas accident du travail (05/02/2026, Q1 retour user prod).
 *
 * Système combinant probabilité cyclique + trigger événementiel :
 *   1. Tick quotidien : chaque chauffeur ACTIF a une chance journalière
 *      d'accident, modulée par fatigue + seniority + nombre de jours
 *      consécutifs sans repos. Base ~0.05 % / jour max → ≈ 3 % / an.
 *   2. Trigger événementiel : pendant un contrat > 300 km avec fatigue
 *      > 70, +0.3 % de chance immédiate sur le tick.
 *
 * 3 outcomes :
 *   • 60 % accident léger : 5-15 j d'arrêt + indemnités modestes.
 *   • 30 % accident grave : 30-60 j d'arrêt + indemnités + rep -3.
 *   • 10 % faute employeur (conditions non sécurisées) → arc juridique :
 *     procédure prud'homale ouverte. Le joueur choisit entre négocier
 *     une transaction amiable (-8 000 €) ou aller au tribunal (15-30 j
 *     plus tard : résolution 50/50 → -25 000 €/-0 €).
 *
 * Le « danger employeur » est inféré : fatigue > 85, pas de formation
 * sécurité dans la dernière année, ou heures supplémentaires fréquentes.
 */

const BASE_DAILY_RISK = 0.0005;        // 0.05 % par jour à fatigue max
const FATIGUE_THRESHOLD = 70;
const FATIGUE_MULTIPLIER_HIGH = 1.6;   // ×1.6 si fatigue > 70
const TRIGGER_DISTANCE_KM = 300;
const TRIGGER_FATIGUE_BONUS = 0.003;   // +0.3 % par tick si trigger

const OUTCOMES = [
  { id: 'light',      probability: 0.60, daysOff: [5, 15],   indemnityRange: [1500, 3000],  repDelta: 0,  fault: false, label: 'Accident léger' },
  { id: 'severe',     probability: 0.30, daysOff: [30, 60],  indemnityRange: [5000, 10000], repDelta: -3, fault: false, label: 'Accident grave' },
  { id: 'employer_fault', probability: 0.10, daysOff: [60, 120], indemnityRange: [10000, 18000], repDelta: -5, fault: true, label: 'Accident grave — faute employeur présumée' },
];

function makeSeededRng(seedStr) {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let s = h >>> 0;
  return function rng() {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickOutcome(rng) {
  let r = rng();
  for (const o of OUTCOMES) {
    if (r < o.probability) return o;
    r -= o.probability;
  }
  return OUTCOMES[0];
}

function pickRangeInt(rng, [min, max]) {
  return min + Math.floor(rng() * (max - min + 1));
}

function isHighRiskMission(driver, vehicles, contracts) {
  if (!driver || driver.status !== 'active') return false;
  const vehicle = vehicles.find(v => v.id === driver.assignedVehicleId);
  if (!vehicle) return false;
  const contract = contracts.find(c => c.instanceId === vehicle.assignedContractId);
  if (!contract) return false;
  // Distance via name heuristic: contract.distance ou contract.km
  const distance = contract.distance || contract.km || 0;
  return distance > TRIGGER_DISTANCE_KM && (driver.fatigue || 0) > FATIGUE_THRESHOLD;
}

export function createWorkAccidentSlice(set, get) {
  return {
    /**
     * État transitoire des contentieux ouverts. Shape :
     *   { [accidentId]: { driverId, driverName, outcome, day, deadline,
     *                      transactionalCost, tribunalRange } }
     * Vidé par `dismissWorkAccidentDispute` ou par le tick (décision tribunal).
     */
    workAccidentDisputes: [],

    /**
     * Tick quotidien — résout les accidents potentiels.
     * Appelé depuis coreSlice.nextDay (après tick contracts).
     */
    tickWorkAccidents: () => {
      const state = get();
      const totalDays = state.totalDays || 0;
      const drivers = state.drivers || [];
      const vehicles = state.vehicles || [];
      const contracts = state.activeContracts || [];

      const updatedDrivers = drivers.slice();
      const updatedVehicles = vehicles.slice();
      const pendingMessages = [];
      let treasuryDelta = 0;
      let reputationDelta = 0;
      const newDisputes = [];
      const notifications = [];

      drivers.forEach((d, idx) => {
        if (d.status !== 'active') return; // seul un chauffeur en mission peut avoir un accident du travail
        // Cooldown post-accident (relâchement du tirage juste après une incident).
        if (d.lastWorkAccidentDay && (totalDays - d.lastWorkAccidentDay) < 60) return;

        const fatigue = d.fatigue || 0;
        let risk = BASE_DAILY_RISK * (fatigue / 100);
        if (fatigue > FATIGUE_THRESHOLD) risk *= FATIGUE_MULTIPLIER_HIGH;
        if (isHighRiskMission(d, vehicles, contracts)) risk += TRIGGER_FATIGUE_BONUS;

        // RNG seedé pour reproductibilité.
        const rng = makeSeededRng(`accident:${d.id}:${totalDays}`);
        if (rng() >= risk) return; // pas d'accident ce jour-ci

        const outcome = pickOutcome(rng);
        const daysOff = pickRangeInt(rng, outcome.daysOff);
        const indemnity = pickRangeInt(rng, outcome.indemnityRange);

        // Met le chauffeur en arrêt + libère son véhicule.
        updatedDrivers[idx] = {
          ...d,
          status: 'sick',
          sickEndDay: totalDays + daysOff,
          assignedVehicleId: null,
          fatigue: 30, // décompresse partiellement à l'arrêt
          lastWorkAccidentDay: totalDays,
          workAccidentHistory: [
            ...(d.workAccidentHistory || []),
            { day: totalDays, outcome: outcome.id, daysOff },
          ],
        };
        // Libère le véhicule
        const vIdx = updatedVehicles.findIndex(v => v.id === d.assignedVehicleId);
        if (vIdx >= 0) {
          updatedVehicles[vIdx] = {
            ...updatedVehicles[vIdx],
            assignedDriverId: null,
            status: 'idle',
          };
        }

        treasuryDelta -= indemnity;
        reputationDelta += outcome.repDelta;

        // Email résumé
        pendingMessages.push({
          senderType: 'advisor',
          senderName: 'Direction HSE',
          tone: outcome.fault ? 'aggressive' : 'formal',
          kind: 'info',
          subject: `${outcome.label} — ${d.name}`,
          body:
            `Notification d'accident du travail.\n\n` +
            `**Chauffeur :** ${d.name}\n` +
            `**Type :** ${outcome.label}\n` +
            `**Arrêt :** ${daysOff} jours\n` +
            `**Indemnités versées :** ${indemnity.toLocaleString('fr-FR')} €\n` +
            `**Impact réputation :** ${outcome.repDelta > 0 ? '+' : ''}${outcome.repDelta}\n\n` +
            (outcome.fault
              ? `⚠️ **Faute employeur présumée** — conditions de travail non conformes ` +
                `(fatigue excessive sans repos, formation sécurité expirée). Procédure ` +
                `prud'homale ouverte. Décision dans 15-30 j.\n\nVoir l'arc juridique pour ` +
                `transiger amiablement ou aller au tribunal.`
              : ''),
          ttlDays: 21,
        });

        notifications.push({
          id: `wa-${d.id}-${totalDays}`,
          type: 'work_accident',
          title: outcome.label,
          message: `${d.name} → ${daysOff} j d'arrêt. Indemnités ${indemnity.toLocaleString('fr-FR')} €.`,
          day: totalDays,
          read: false,
        });

        // Faute employeur → ouvre un litige (arc juridique).
        if (outcome.fault) {
          newDisputes.push({
            id: `wad-${d.id}-${totalDays}`,
            driverId: d.id,
            driverName: d.name,
            openedOn: totalDays,
            deadline: totalDays + pickRangeInt(rng, [15, 30]),
            transactionalCost: pickRangeInt(rng, [6000, 10000]),
            tribunalLossCost: pickRangeInt(rng, [20000, 30000]),
            // 50 % de gagner au tribunal (RNG seedé sur disputeId pour cohérence).
            tribunalWinProbability: 0.5,
            status: 'open',
          });
        }
      });

      if (treasuryDelta === 0 && newDisputes.length === 0 && updatedDrivers === drivers) {
        // Pas d'accident ce tick — quand-même résoudre les contentieux ouverts.
      }

      // 2) Résoudre les contentieux dont le délai est dépassé.
      const stillOpenDisputes = [];
      const existingDisputes = state.workAccidentDisputes || [];
      existingDisputes.forEach(dispute => {
        if (dispute.status !== 'open' && dispute.status !== 'tribunal') {
          stillOpenDisputes.push(dispute);
          return;
        }
        if (dispute.status === 'tribunal' && totalDays >= dispute.tribunalDecisionDay) {
          // Décision tribunal
          const rng = makeSeededRng(`tribunal:${dispute.id}`);
          const won = rng() < dispute.tribunalWinProbability;
          if (won) {
            pendingMessages.push({
              senderType: 'advisor',
              senderName: 'Cabinet juridique',
              tone: 'friendly',
              kind: 'info',
              subject: `Tribunal — décision en votre faveur (${dispute.driverName})`,
              body: `Le tribunal a tranché en votre faveur pour le contentieux ${dispute.driverName}. Pas de pénalité supplémentaire (frais d'avocat 2 000 € déjà débités).`,
              ttlDays: 14,
            });
            treasuryDelta -= 2000;
          } else {
            treasuryDelta -= dispute.tribunalLossCost;
            reputationDelta -= 3;
            pendingMessages.push({
              senderType: 'advisor',
              senderName: 'Cabinet juridique',
              tone: 'aggressive',
              kind: 'info',
              subject: `Tribunal — condamnation (${dispute.driverName})`,
              body: `Le tribunal a condamné la société pour le contentieux ${dispute.driverName} : ${dispute.tribunalLossCost.toLocaleString('fr-FR')} € de dommages-intérêts + réputation -3. Le verdict est public.`,
              ttlDays: 21,
            });
          }
        } else {
          stillOpenDisputes.push(dispute);
        }
      });

      const finalDisputes = [...stillOpenDisputes, ...newDisputes];

      // Apply state.
      const next = {
        drivers: updatedDrivers,
        vehicles: updatedVehicles,
        treasury: (state.treasury || 0) + treasuryDelta,
        reputation: Math.max(0, Math.min(100, (state.reputation || 50) + reputationDelta)),
        workAccidentDisputes: finalDisputes,
      };
      if (notifications.length) {
        next.notifications = [...(state.notifications || []).slice(-30), ...notifications];
      }
      set(next);

      // Push messages APRÈS set() (évite race avec inbox state).
      if (typeof get().pushCustomMessage === 'function') {
        pendingMessages.forEach(m => {
          try { get().pushCustomMessage(m); }
          catch (err) { console.warn('[workAccident] push msg failed:', err); }
        });
      }

      return {
        accidents: notifications.length,
        disputesOpened: newDisputes.length,
        disputesResolved: existingDisputes.length - stillOpenDisputes.length,
      };
    },

    /**
     * Joueur décide de transiger amiablement pour un contentieux ouvert.
     * Débit immédiat = transactionalCost. Clôt le dispute.
     */
    settleWorkAccidentDispute: (disputeId) => {
      const state = get();
      const dispute = (state.workAccidentDisputes || []).find(d => d.id === disputeId);
      if (!dispute || dispute.status !== 'open') return { ok: false, reason: 'not_open' };
      if ((state.treasury || 0) < dispute.transactionalCost) return { ok: false, reason: 'insufficient_funds' };
      set({
        treasury: state.treasury - dispute.transactionalCost,
        workAccidentDisputes: state.workAccidentDisputes.map(d =>
          d.id === disputeId ? { ...d, status: 'settled' } : d
        ),
      });
      if (typeof get().pushCustomMessage === 'function') {
        get().pushCustomMessage({
          senderType: 'advisor',
          senderName: 'Cabinet juridique',
          tone: 'formal',
          kind: 'info',
          subject: `Transaction amiable signée — ${dispute.driverName}`,
          body: `Transaction de ${dispute.transactionalCost.toLocaleString('fr-FR')} € signée pour le contentieux ${dispute.driverName}. Le dossier est clos.`,
          ttlDays: 14,
        });
      }
      return { ok: true, cost: dispute.transactionalCost };
    },

    /**
     * Joueur décide d'aller au tribunal pour un contentieux ouvert.
     * Pas de débit immédiat, mais 50 % de risque de payer tribunalLossCost
     * dans 15-30 jours (résolution dans le tick).
     */
    goToTribunalWorkAccident: (disputeId) => {
      const state = get();
      const dispute = (state.workAccidentDisputes || []).find(d => d.id === disputeId);
      if (!dispute || dispute.status !== 'open') return { ok: false, reason: 'not_open' };
      const totalDays = state.totalDays || 0;
      set({
        workAccidentDisputes: state.workAccidentDisputes.map(d =>
          d.id === disputeId ? {
            ...d,
            status: 'tribunal',
            tribunalDecisionDay: totalDays + 15 + Math.floor(Math.random() * 16), // 15-30 j
          } : d
        ),
      });
      return { ok: true };
    },

    getOpenWorkAccidentDisputes: () => (get().workAccidentDisputes || []).filter(d => d.status === 'open'),
  };
}

export const _WORK_ACCIDENT_INTERNAL = {
  OUTCOMES,
  BASE_DAILY_RISK,
  FATIGUE_THRESHOLD,
  FATIGUE_MULTIPLIER_HIGH,
  TRIGGER_DISTANCE_KM,
  TRIGGER_FATIGUE_BONUS,
};
